import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Table } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useEmpGroupForm } from './hooks/useEmpGroupForm';
import type { MemberRequest } from '../../services/empGroupApi';

const P910TForm: React.FC = () => {
  const form = useEmpGroupForm();
  const [searchText, setSearchText] = useState('');
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyCode, setCopyCode] = useState('');

  const handleDelete = () => {
    if (!form.current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除群組「${form.current.f90t_01}」及所有成員？`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: form.doDelete,
    });
  };

  const handleCopy = () => {
    if (!form.current) return;
    setCopyCode(''); setCopyModalOpen(true);
  };

  const modeLabel = form.mode === 'add' ? '新增' : form.mode === 'edit' ? '修改' : '瀏覽';

  const memberColumns = [
    { title: '序', width: 50, render: (_: unknown, __: MemberRequest, i: number) => i + 1 },
    { title: '員工代號', dataIndex: 'f90t1_11', width: 150,
      render: (v: string, _: MemberRequest, i: number) => form.isEditing
        ? <Input size="small" value={v} onChange={e => form.updateMember(i, 'f90t1_11', e.target.value)} maxLength={8} />
        : v },
    { title: '員工姓名', dataIndex: 'f90t1_11a',
      render: (v: string, _: MemberRequest, i: number) => form.isEditing
        ? <Input size="small" value={v} onChange={e => form.updateMember(i, 'f90t1_11a', e.target.value)} maxLength={12} />
        : v },
    ...(form.isEditing ? [{
      title: '', width: 40,
      render: (_: unknown, __: MemberRequest, i: number) => (
        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => form.removeMember(i)} />
      ),
    }] : []),
  ];

  return (
    <Spin spinning={form.loading}>
      <div className="max-w-3xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          員工群組設定 (pw910tf1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {form.records.length > 0 ? `${form.currentIdx + 1} / ${form.records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search + Nav */}
        <div className="flex items-center gap-2 mb-3">
          <Input placeholder="輸入群組代號查詢" value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => searchText.trim() && form.quickSearch(searchText.trim())}
            disabled={form.isEditing} style={{ width: 250 }} />
          <Button onClick={() => searchText.trim() && form.quickSearch(searchText.trim())}
            disabled={form.isEditing}>查詢</Button>
          <span className="mx-2" />
          <Button size="small" onClick={form.navFirst} disabled={form.isEditing || form.records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={form.navPrev} disabled={form.isEditing || form.currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={form.navNext} disabled={form.isEditing || form.currentIdx >= form.records.length - 1}>&gt;</Button>
          <Button size="small" onClick={form.navLast} disabled={form.isEditing || form.records.length === 0}>&gt;|</Button>
        </div>

        {/* Master Fields */}
        <div className="flex gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">群組代號</label>
            <Input value={form.code} onChange={e => form.setCode(e.target.value)} maxLength={12}
              disabled={form.mode !== 'add'} style={{ width: 200 }} />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">群組說明</label>
            <Input value={form.desc} onChange={e => form.setDesc(e.target.value)} maxLength={60}
              disabled={!form.isEditing} />
          </div>
          {form.detail && form.mode === 'browse' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">成員數</label>
              <span className="text-lg font-bold">{form.detail.f90t_21}</span>
            </div>
          )}
        </div>

        {/* Members Grid */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">群組成員</span>
            {form.isEditing && form.members.length < 99 && (
              <Button size="small" icon={<PlusOutlined />} onClick={form.addMember}>新增成員</Button>
            )}
          </div>
          <Table dataSource={form.members} columns={memberColumns} rowKey={(_, i) => String(i)}
            size="small" pagination={false} scroll={{ y: 300 }} />
        </div>

        {/* Audit */}
        {form.detail && form.mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-4">
            建立: {form.detail.f90t_91} | 日期: {form.detail.f90t_92 || '-'} | 修改: {form.detail.f90t_93}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          {!form.isEditing ? (
            <>
              <Button type="primary" onClick={form.startAdd}>新增</Button>
              <Button onClick={form.startEdit} disabled={!form.current}>修改</Button>
              <Button onClick={handleCopy} disabled={!form.current}>複製</Button>
              <Button danger onClick={handleDelete} disabled={!form.current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={form.save}>儲存</Button>
              <Button onClick={form.cancel}>取消</Button>
            </>
          )}
        </div>

        <Modal title="複製群組" open={copyModalOpen}
          onOk={async () => { if (copyCode.trim()) { await form.doCopy(copyCode.trim()); setCopyModalOpen(false); } }}
          onCancel={() => setCopyModalOpen(false)} okText="確定" cancelText="取消">
          <p className="mb-2">來源: {form.current?.f90t_01} {form.current?.f90t_11}</p>
          <Input placeholder="請輸入新群組代號" value={copyCode}
            onChange={e => setCopyCode(e.target.value)} maxLength={12} />
        </Modal>
      </div>
    </Spin>
  );
};

export default P910TForm;
